import { createSelector } from 'reselect'
import { pathOr } from 'ramda'
import moment from 'moment'

import { formatTime } from './utils'

const getUserAddresses = state =>
  pathOr(null, ['api', 'subscription', 'info', 'addresses'], state)

export const getAddresses = createSelector([getUserAddresses], addresses => {
  if (addresses) {
    const defaultAddress = addresses.slice(0, 1).map(address => {
      const streetComponents =
        (address.street && address.street.split('\n')) || []
      const street = streetComponents.length > 0 ? streetComponents[0] : ''
      const apt = streetComponents.length > 1 ? streetComponents[1] : ''
      const { city, region } = address
      return `${street ? street : ''} ${apt ? apt : ''}, ${city}, ${region}`
    })

    return defaultAddress.length ? defaultAddress[0] : null
  }

  return null
})

const getProfilesInfo = state =>
  pathOr(null, ['api', 'subscription', 'info', 'profiles'], state)

export const getProfiles = createSelector(
  [getProfilesInfo],
  profiles => profiles
)

const getUpcomingOrderStatus = state =>
  pathOr({}, ['api', 'upcommingOrderStatus'], state)

export const getMealsInDelivery = state =>
  pathOr(0, ['api', 'subscription', 'userPlan', 'mealsPerDelivery'], state)
export const getInvoicesInFlight = state =>
  pathOr(true, ['api', 'user', 'invoicesInFlight'], state)

export const isLoading = createSelector(
  [getInvoicesInFlight, getUpcomingOrderStatus],
  (invoicesInFlight, upcomingOrderStatus) =>
    invoicesInFlight || upcomingOrderStatus.inFlight
)

export const getInvoicesSucceed = state =>
  pathOr(true, ['api', 'user', 'invoicesSucceed'], state)

const getStatus = invoice => {
  const order =
    invoice.orders && invoice.orders.length ? invoice.orders[0] : null
  const delivery =
    order &&
    moment(`${order.delivery_date} ${order.time_end}`, 'YYYY-MM-DD HH:mm')
  const delivered = order && moment().isAfter(delivery)

  if (!invoice.charges.length) {
    return { shipped: true, delivered }
  }
  if (
    invoice.charges.length &&
    invoice.charges.find(c => c.refund.length > 0)
  ) {
    const { refund } = invoice.charges.find(c => c.refund.length > 0)
    return {
      refunded: `Refunded ${refund[0].amount ? `$${refund[0].amount}` : ''}`,
      delivered
    }
  }
  if (invoice.charges.filter(c => c.status === 'accepted').length) {
    return { paid: true, delivered }
  }
  if (invoice.charges.find(c => c.status === 'rejected')) {
    return { failed: true, delivered }
  }

  return { shipped: true, delivered }
}

const getInvoices = state => pathOr([], ['api', 'user', 'invoices'], state)

export const getHasMore = state =>
  pathOr(false, ['api', 'user', 'hasMore'], state)

export const getShippedOrders = createSelector([getInvoices], invoices => {
  const shippedOrders = invoices.map(invoice => {
    const [deliveryAddress1, deliveryAddress2] = invoice.deliveryAddress.split(
      '\n'
    )

    const order = invoice.orders.length ? invoice.orders[0] : null
    const date = order && order.display_date
    const dateOrdered = invoice.date
    const timeslot = order && {
      start: order.time_start,
      end: order.time_end,
      label: `${formatTime(order.time_start)} - ${formatTime(order.time_end)}`
    }
    const status = getStatus(invoice)

    return {
      id: invoice.id,
      invoice: {
        ...invoice,
        deliveryAddress1,
        deliveryAddress2,
        billingDate: moment(invoice.date, 'YYYY-MM-DD').format('DD/MM/YYYY')
      },
      date,
      dateOrdered,
      timeslot,
      order,
      total: invoice.total,
      ...status
    }
  })

  return shippedOrders
})

const getSettings = state =>
  pathOr([], ['api', 'subscription', 'settings'], state)

export const showRateMealJourney = createSelector([getSettings], settings => {
  const showRateMealJourney = settings.find(
    i => i.key === 'showratemealjourney'
  )
  return typeof showRateMealJourney === 'undefined'
})
